import { useState, useEffect } from "react";

import {
  SidebarNavigationContainer,
  SidebarNavigationItem,
  ArrowButton,
  Typography,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as FaceMan } from "@king-ict/eupisi-ui/components/Icon/FaceMan.svg";
import { EnrollmentTypeEnum } from "@king-ict/eupisi-ui/models";
import { useTranslation } from "react-i18next";
import { useLocation, useRouteMatch } from "react-router";

import { getRequestPersonInformation as getRequestPersonInformationApi } from "../../api";
import {
  homeRoute,
  criteriaRoute,
  newCriterionRoute,
  editCriterionRoute,
  capacitiesRoute,
  requestsRoute,
  benfitsRoute,
  newBenefitRoute,
  detailsBenefitRoute,
  subsequentRequestsRoute,
  enrollmentListRoute,
  confirmedEnrollmentListRoute,
  requestDetailsRoute,
  subsequentRequestDetailsRoute,
  newCapacityRoute,
  editCapacityRoute,
  adminWebUserPortalAplicationUrl,
  rejectedEnrollmentListRoute,
  newSubsequentCapacityRoute,
  editSubsequentCapacityRoute,
  subsequentCapacitiesRoute,
  importantDateListRoute,
  editImportantDateRoute,
  subsequentImportantDateListRoute,
  editSubsequentImportantDateRoute,
  preschoolRequestDetailsRoute,
  preschoolRequestsRoute,
  subsequentEnrollmentListRoute,
  subsequentConfirmedEnrollmentListRoute,
  subsequentRejectedEnrollmentListRoute,
  preschoolEnrollmentListRoute,
  preschoolConfirmedEnrollmentListRoute,
  preschoolRejectedEnrollmentListRoute,
  beneficiaryListRoute,
  useTypedSelector,
  aboutProgramRoute,
  initialConversationRoute,
} from "../../app";
import {
  HomePage,
  CriterionList,
  CriterionCreateUpdate,
  BenefitList,
  BenefitCreate,
  RegularEnrollmentLocationProgramCreateUpdate,
  RegularEnrollmentLocationProgramList,
  RegularEnrollmentRequestList,
  SubsequentEnrollmentRequestList,
  EnrollmentListPage,
  ConfirmedEnrollmentListPage,
  RequestBasicInfoPage,
  RequestLayout,
  RequestCriteriaPage,
  LocationProgramDetails,
  RequestInitialConversationPage,
  RejectedEnrollmentListPage,
  SubsequentEnrollmentLocationProgramCreateUpdate,
  SubsequentEnrollmentLocationProgramList,
  RequestPhysicalExaminationPage,
  RequestPhysicalExaminationPageDocumentUpload,
  RegularImportantDateList,
  RegularImportantDateUpdate,
  SubsequentImportantDateList,
  SubsequentImportantDateUpdate,
  BeneficiaryList,
} from "../../features";
import AboutProgram from "../../features/AboutProgram/AboutProgram";
import InitialConversation from "../../features/InitialConversation/InitialConversation";
import IzjavaOPristupacnosti from "../../features/IzjavaOPristupacnosti/IzjavaOPristupacnosti";
import { useBusinessRule, usePermission } from "../../hooks";
import { PermissionEnum, RequestTypeEnum } from "../../models";
import { RequestPersonInfo } from "../../models";
import styles from "./Sidebar.module.scss";

export interface MatchParams {
  requestId: string;
  id: string;
}

export interface SidebarProps {
  showAboutPage: boolean;
  showIzjavaOPristupacnostiPage: boolean;
}

export const Sidebar = ({
  showAboutPage,
  showIzjavaOPristupacnostiPage,
}: SidebarProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { hasPermission } = usePermission();
  const [requestPersonInfo, setRequestPersonInfo] = useState<RequestPersonInfo>(
    {} as RequestPersonInfo
  );
  const user = useTypedSelector((state) => state.application.user);
  const requestMatch = useRouteMatch<MatchParams>(requestDetailsRoute);
  const subsequentRequestMatch = useRouteMatch<MatchParams>(
    subsequentRequestDetailsRoute
  );

  const preschoolRequestMatch = useRouteMatch<MatchParams>(
    preschoolRequestDetailsRoute
  );

  const newCriterionMatch = useRouteMatch<MatchParams>(newCriterionRoute);
  const editCriterionMatch = useRouteMatch<MatchParams>(editCriterionRoute);

  const newCapacityMatch = useRouteMatch<MatchParams>(newCapacityRoute);
  const editCapacityMatch = useRouteMatch<MatchParams>(editCapacityRoute);
  const newBenefitMatch = useRouteMatch<MatchParams>(newBenefitRoute);
  const detailsBenefitMatch = useRouteMatch<MatchParams>(detailsBenefitRoute);

  const newSubsequentCapacityMatch = useRouteMatch<MatchParams>(
    newSubsequentCapacityRoute
  );
  const editSubsequentCapacityMatch = useRouteMatch<MatchParams>(
    editSubsequentCapacityRoute
  );
  const editImportantDateMatch = useRouteMatch<MatchParams>(
    editImportantDateRoute
  );

  const editSubsequentImportantDateMatch = useRouteMatch<MatchParams>(
    editSubsequentImportantDateRoute
  );

  const {
    canReadBeneficiaryList,
    canReadImportantDates,
    canPhysicalExaminationDocumentUpload,
  } = useBusinessRule();

  const canUploadPhysicalExaminationDocument = () => {
    return (
      canPhysicalExaminationDocumentUpload() &&
      requestPersonInfo.createdByAppUserId != null &&
      requestPersonInfo.createdByAppUserId === user.userId
    );
  };

  useEffect(() => {
    (async () => {
      if (
        requestMatch?.params.requestId ||
        subsequentRequestMatch?.params.requestId ||
        preschoolRequestMatch?.params.requestId
      ) {
        const requestPersonInfo = await getRequestPersonInformationApi(
          requestMatch?.params.requestId
            ? +requestMatch?.params.requestId
            : subsequentRequestMatch?.params.requestId
            ? +subsequentRequestMatch?.params.requestId!
            : +preschoolRequestMatch?.params.requestId!
        );
        setRequestPersonInfo(requestPersonInfo);
      }
    })();
  }, [
    requestMatch?.params.requestId,
    subsequentRequestMatch?.params.requestId,
    preschoolRequestMatch?.params.requestId,
  ]);

  const handleBackToEUpisi = () => {
    window.location.href = `${adminWebUserPortalAplicationUrl}`;
  };
  const getCriteriaItems = () => {
    let route = "";
    let url = undefined;
    let routeComponent = <></>;

    if (newCriterionMatch) {
      route = newCriterionRoute;
      routeComponent = <CriterionCreateUpdate />;
    } else if (editCriterionMatch) {
      route = editCriterionRoute;
      url = editCriterionMatch.url;
      routeComponent = <CriterionCreateUpdate />;
    } else {
      route = criteriaRoute;
      routeComponent = <CriterionList />;
    }

    return (
      <SidebarNavigationItem
        name={t("sidebar.scoringCriteria")}
        route={route}
        url={url}
      >
        {routeComponent}
      </SidebarNavigationItem>
    );
  };

  const getImportantDateItems = () => {
    let route = "";
    let url = undefined;
    let routeComponent = <></>;

    if (editImportantDateMatch) {
      route = editImportantDateRoute;
      url = editImportantDateMatch.url;
      routeComponent = <RegularImportantDateUpdate />;
    } else {
      route = importantDateListRoute;
      routeComponent = <RegularImportantDateList />;
    }

    return (
      <SidebarNavigationItem
        name={t("sidebar.regularEnrollment")}
        route={route}
        url={url}
      >
        {routeComponent}
      </SidebarNavigationItem>
    );
  };

  const getSubsequentImportantDateItems = () => {
    let route = "";
    let url = undefined;
    let routeComponent = <></>;

    if (editSubsequentImportantDateMatch) {
      route = editSubsequentImportantDateRoute;
      url = editSubsequentImportantDateMatch.url;
      routeComponent = <SubsequentImportantDateUpdate />;
    } else {
      route = subsequentImportantDateListRoute;
      routeComponent = <SubsequentImportantDateList />;
    }

    return (
      <SidebarNavigationItem
        name={t("sidebar.subsequentEnrollment")}
        route={route}
        url={url}
      >
        {routeComponent}
      </SidebarNavigationItem>
    );
  };

  const geBeneficiaryListItems = () => {
    let route = "";
    let url = undefined;
    let routeComponent = <></>;

    route = beneficiaryListRoute;
    routeComponent = <BeneficiaryList />;

    return (
      <SidebarNavigationItem
        name={t("sidebar.beneficiaryList")}
        route={route}
        url={url}
      >
        {routeComponent}
      </SidebarNavigationItem>
    );
  };

  // const getBenefitsItems = () => {
  //   let route = "";
  //   let url = undefined;
  //   let routeComponent = <></>;

  //   if (newBenefitMatch) {
  //     route = newBenefitRoute;
  //     routeComponent = <BenefitCreate />;
  //   } else if (detailsBenefitMatch) {
  //     route = detailsBenefitRoute;
  //     url = detailsBenefitMatch.url;
  //     routeComponent = <BenefitCreate />;
  //   } else {
  //     route = benfitsRoute;
  //     routeComponent = <BenefitList />;
  //   }

  //   return (
  //     <SidebarNavigationItem
  //       name={t("sidebar.benefitCriteria")}
  //       route={route}
  //       url={url}
  //     >
  //       {routeComponent}
  //     </SidebarNavigationItem>
  //   );
  // };

  // TO DO: Uncomment when benefits needs to be delivered to client
  // const getBenefitsItems = () => {
  //   let route = "";
  //   let url = undefined;
  //   let routeComponent = <></>;

  //   if (newBenefitMatch) {
  //     route = newBenefitRoute;
  //     routeComponent = <BenefitCreate />;
  //   } else if (detailsBenefitMatch) {
  //     route = detailsBenefitRoute;
  //     url = detailsBenefitMatch.url;
  //     routeComponent = <BenefitCreate />;
  //   } else {
  //     route = benfitsRoute;
  //     routeComponent = <BenefitList />;
  //   }

  //   return (
  //     <SidebarNavigationItem
  //       name={t("sidebar.benefitCriteria")}
  //       route={route}
  //       url={url}
  //     >
  //       {routeComponent}
  //     </SidebarNavigationItem>
  //   );
  // };

  const getCapacityItems = () => {
    let route = "";
    let url = undefined;
    let routeComponent = <></>;

    if (newCapacityMatch) {
      route = newCapacityRoute;
      routeComponent = <RegularEnrollmentLocationProgramCreateUpdate />;
    } else if (editCapacityMatch) {
      route = editCapacityRoute;
      url = editCapacityMatch.url;
      routeComponent = <RegularEnrollmentLocationProgramCreateUpdate />;
    } else {
      route = capacitiesRoute;
      routeComponent = <RegularEnrollmentLocationProgramList />;
    }

    return (
      <SidebarNavigationItem
        name={t("sidebar.regularEnrollmentCapacities")}
        route={route}
        url={url}
      >
        {routeComponent}
      </SidebarNavigationItem>
    );
  };

  const getSubsequentCapacityItems = () => {
    let route = "";
    let url = undefined;
    let routeComponent = <></>;

    if (newSubsequentCapacityMatch) {
      route = newSubsequentCapacityRoute;
      routeComponent = <SubsequentEnrollmentLocationProgramCreateUpdate />;
    } else if (editSubsequentCapacityMatch) {
      route = editSubsequentCapacityRoute;
      url = editSubsequentCapacityMatch.url;
      routeComponent = <SubsequentEnrollmentLocationProgramCreateUpdate />;
    } else {
      route = subsequentCapacitiesRoute;
      routeComponent = <SubsequentEnrollmentLocationProgramList />;
    }

    return (
      <SidebarNavigationItem
        name={t("sidebar.subsequentEnrollmentCapacities")}
        route={route}
        url={url}
      >
        {routeComponent}
      </SidebarNavigationItem>
    );
  };

  const sidebarTitle = () => {
    return (
      <>
        {requestPersonInfo.person && (
          <div className={styles.subHeader}>
            <div>
              <FaceMan />
              <span className={styles.nameTitle}>{`${
                requestPersonInfo.person?.firstName ?? ""
              } ${requestPersonInfo.person?.lastName ?? ""}`}</span>
            </div>
          </div>
        )}
      </>
    );
  };

  const getRequestItems = () => {
    return (
      <SidebarNavigationItem
        key={`navigation_requestListFirst`}
        name={t("sidebar.regularEnrollmentRequests")}
        route={requestsRoute}
      >
        <RegularEnrollmentRequestList
          requestTypeId={RequestTypeEnum.Kindergarten}
        />
      </SidebarNavigationItem>
    );
  };

  const getSubsequentRequestItems = () => {
    return (
      <SidebarNavigationItem
        key={`navigation_requestListSecond`}
        name={t("sidebar.subsequentEnrollmentRequests")}
        route={subsequentRequestsRoute}
      >
        <SubsequentEnrollmentRequestList />
      </SidebarNavigationItem>
    );
  };

  const getRequestDetailsItems = () => {
    return (
      <SidebarNavigationItem
        key={`navigation_requestList`}
        name={t("sidebar.regularEnrollmentRequests")}
        expanded
        arrowHiden
        clickable={false}
        customTitle={sidebarTitle()}
      >
        <SidebarNavigationItem
          key={`navigation_${requestMatch?.path}basic-info`}
          name={t("sidebar.basicInfo")}
          route={
            requestMatch?.path.endsWith("/")
              ? `${requestMatch?.path}basic-info`
              : `${requestMatch?.path}/basic-info`
          }
          url={
            requestMatch?.url.endsWith("/")
              ? `${requestMatch?.url}basic-info`
              : `${requestMatch?.url}/basic-info`
          }
        >
          <RequestLayout requestPersonInfo={requestPersonInfo}>
            <RequestBasicInfoPage></RequestBasicInfoPage>
          </RequestLayout>
        </SidebarNavigationItem>
        <SidebarNavigationItem
          name={t("sidebar.locationPrograms")}
          route={
            requestMatch?.path.endsWith("/")
              ? `${requestMatch?.path}location-programs`
              : `${requestMatch?.path}/location-programs`
          }
          url={
            requestMatch?.url.endsWith("/")
              ? `${requestMatch?.url}location-programs`
              : `${requestMatch?.url}/location-programs`
          }
        >
          <RequestLayout requestPersonInfo={requestPersonInfo}>
            <LocationProgramDetails />
          </RequestLayout>
        </SidebarNavigationItem>
        <SidebarNavigationItem
          name={t("sidebar.criteriaAndPoints")}
          route={
            requestMatch?.path.endsWith("/")
              ? `${requestMatch?.path}criteria`
              : `${requestMatch?.path}/criteria`
          }
          url={
            requestMatch?.url.endsWith("/")
              ? `${requestMatch?.url}criteria`
              : `${requestMatch?.url}/criteria`
          }
        >
          <RequestLayout requestPersonInfo={requestPersonInfo}>
            <RequestCriteriaPage
              requestPersonInfo={requestPersonInfo}
            ></RequestCriteriaPage>
          </RequestLayout>
        </SidebarNavigationItem>
        <SidebarNavigationItem
          name={t("sidebar.initialConversation")}
          route={
            requestMatch?.path.endsWith("/")
              ? `${requestMatch?.path}initial-conversation`
              : `${requestMatch?.path}/initial-conversation`
          }
          url={
            requestMatch?.url.endsWith("/")
              ? `${requestMatch?.url}initial-conversation`
              : `${requestMatch?.url}/initial-conversation`
          }
        >
          <RequestLayout requestPersonInfo={requestPersonInfo}>
            <RequestInitialConversationPage
              requestPersonInfo={requestPersonInfo}
            ></RequestInitialConversationPage>
          </RequestLayout>
        </SidebarNavigationItem>
        {hasPermission(PermissionEnum.PhysicalExaminationRead) ? (
          <SidebarNavigationItem
            name={t("sidebar.physicalExamination")}
            route={
              requestMatch?.path.endsWith("/")
                ? `${requestMatch?.path}physical-examination`
                : `${requestMatch?.path}/physical-examination`
            }
            url={
              requestMatch?.url.endsWith("/")
                ? `${requestMatch?.url}physical-examination`
                : `${requestMatch?.url}/physical-examination`
            }
          >
            <RequestLayout requestPersonInfo={requestPersonInfo}>
              <RequestPhysicalExaminationPage
                requestPersonInfo={requestPersonInfo}
              ></RequestPhysicalExaminationPage>
            </RequestLayout>
          </SidebarNavigationItem>
        ) : null}

        {canUploadPhysicalExaminationDocument() ? (
          <SidebarNavigationItem
            name={t("sidebar.physicalExaminationDocumentUpload")}
            route={
              requestMatch?.path.endsWith("/")
                ? `${requestMatch?.path}physical-examination`
                : `${requestMatch?.path}/physical-examination`
            }
            url={
              requestMatch?.url.endsWith("/")
                ? `${requestMatch?.url}physical-examination`
                : `${requestMatch?.url}/physical-examination`
            }
          >
            <RequestLayout requestPersonInfo={requestPersonInfo}>
              <RequestPhysicalExaminationPageDocumentUpload
                requestPersonInfo={requestPersonInfo}
              ></RequestPhysicalExaminationPageDocumentUpload>
            </RequestLayout>
          </SidebarNavigationItem>
        ) : null}
      </SidebarNavigationItem>
    );
  };

  const getSequentRequestDetailsItems = () => {
    return (
      <SidebarNavigationItem
        key={`navigation_subsequentRequestList`}
        name={t("sidebar.subsequentEnrollmentRequests")}
        expanded
        arrowHiden
        clickable={false}
        customTitle={sidebarTitle()}
      >
        <SidebarNavigationItem
          key={`navigation_${subsequentRequestMatch?.path}basic-info`}
          name={t("sidebar.basicInfo")}
          route={
            subsequentRequestMatch?.path.endsWith("/")
              ? `${subsequentRequestMatch?.path}basic-info`
              : `${subsequentRequestMatch?.path}/basic-info`
          }
          url={
            subsequentRequestMatch?.url.endsWith("/")
              ? `${subsequentRequestMatch?.url}basic-info`
              : `${subsequentRequestMatch?.url}/basic-info`
          }
        >
          <RequestLayout requestPersonInfo={requestPersonInfo}>
            <RequestBasicInfoPage></RequestBasicInfoPage>
          </RequestLayout>
        </SidebarNavigationItem>
        <SidebarNavigationItem
          name={t("sidebar.locationPrograms")}
          route={
            subsequentRequestMatch?.path.endsWith("/")
              ? `${subsequentRequestMatch?.path}location-programs`
              : `${subsequentRequestMatch?.path}/location-programs`
          }
          url={
            subsequentRequestMatch?.url.endsWith("/")
              ? `${subsequentRequestMatch?.url}location-programs`
              : `${subsequentRequestMatch?.url}/location-programs`
          }
        >
          <RequestLayout requestPersonInfo={requestPersonInfo}>
            <LocationProgramDetails />
          </RequestLayout>
        </SidebarNavigationItem>
        <SidebarNavigationItem
          name={t("sidebar.criteriaAndPoints")}
          route={
            subsequentRequestMatch?.path.endsWith("/")
              ? `${subsequentRequestMatch?.path}criteria`
              : `${subsequentRequestMatch?.path}/criteria`
          }
          url={
            subsequentRequestMatch?.url.endsWith("/")
              ? `${subsequentRequestMatch?.url}criteria`
              : `${subsequentRequestMatch?.url}/criteria`
          }
        >
          <RequestLayout requestPersonInfo={requestPersonInfo}>
            <RequestCriteriaPage
              requestPersonInfo={requestPersonInfo}
            ></RequestCriteriaPage>
          </RequestLayout>
        </SidebarNavigationItem>

        <SidebarNavigationItem
          name={t("sidebar.initialConversation")}
          route={
            subsequentRequestMatch?.path.endsWith("/")
              ? `${subsequentRequestMatch?.path}initial-conversation`
              : `${subsequentRequestMatch?.path}/initial-conversation`
          }
          url={
            subsequentRequestMatch?.url.endsWith("/")
              ? `${subsequentRequestMatch?.url}initial-conversation`
              : `${subsequentRequestMatch?.url}/initial-conversation`
          }
        >
          <RequestLayout requestPersonInfo={requestPersonInfo}>
            <RequestInitialConversationPage
              requestPersonInfo={requestPersonInfo}
            ></RequestInitialConversationPage>
          </RequestLayout>
        </SidebarNavigationItem>
        {hasPermission(PermissionEnum.PhysicalExaminationRead) ? (
          <SidebarNavigationItem
            name={t("sidebar.physicalExamination")}
            route={
              subsequentRequestMatch?.path.endsWith("/")
                ? `${subsequentRequestMatch?.path}physical-examination`
                : `${subsequentRequestMatch?.path}/physical-examination`
            }
            url={
              requestMatch?.url.endsWith("/")
                ? `${subsequentRequestMatch?.url}physical-examination`
                : `${subsequentRequestMatch?.url}/physical-examination`
            }
          >
            <RequestLayout requestPersonInfo={requestPersonInfo}>
              <RequestPhysicalExaminationPage
                requestPersonInfo={requestPersonInfo}
              ></RequestPhysicalExaminationPage>
            </RequestLayout>
          </SidebarNavigationItem>
        ) : null}

        {canUploadPhysicalExaminationDocument() ? (
          <SidebarNavigationItem
            name={t("sidebar.physicalExaminationDocumentUpload")}
            route={
              subsequentRequestMatch?.path.endsWith("/")
                ? `${subsequentRequestMatch?.path}physical-examination`
                : `${subsequentRequestMatch?.path}/physical-examination`
            }
            url={
              requestMatch?.url.endsWith("/")
                ? `${subsequentRequestMatch?.url}physical-examination`
                : `${subsequentRequestMatch?.url}/physical-examination`
            }
          >
            <RequestLayout requestPersonInfo={requestPersonInfo}>
              <RequestPhysicalExaminationPageDocumentUpload
                requestPersonInfo={requestPersonInfo}
              ></RequestPhysicalExaminationPageDocumentUpload>
            </RequestLayout>
          </SidebarNavigationItem>
        ) : null}
      </SidebarNavigationItem>
    );
  };

  const getEnrollmentList = () => {
    return (
      <SidebarNavigationItem
        name={t("sidebar.regularEnrollment")}
        key={`navigation_regularEnrollment`}
        expanded={
          pathname === enrollmentListRoute ||
          pathname === confirmedEnrollmentListRoute ||
          pathname === rejectedEnrollmentListRoute
        }
      >
        <SidebarNavigationItem
          key={`navigation_enrollmentListChildren`}
          name={t("sidebar.enrollmentChildren")}
          route={enrollmentListRoute}
        >
          <EnrollmentListPage
            enrollmentTypeId={EnrollmentTypeEnum.RegularEnrollment}
            requestTypeId={RequestTypeEnum.Kindergarten}
          />
        </SidebarNavigationItem>
        <SidebarNavigationItem
          key="navigation_confirmedEnrollmentList"
          name={t("sidebar.confirmedEnrollmentList")}
          route={confirmedEnrollmentListRoute}
        >
          <ConfirmedEnrollmentListPage
            enrollmentTypeId={EnrollmentTypeEnum.RegularEnrollment}
            requestTypeId={RequestTypeEnum.Kindergarten}
          />
        </SidebarNavigationItem>
        <SidebarNavigationItem
          key="navigation_rejectedEnrollmentList"
          name={t("sidebar.rejectedEnrollmentList")}
          route={rejectedEnrollmentListRoute}
        >
          <RejectedEnrollmentListPage
            enrollmentTypeId={EnrollmentTypeEnum.RegularEnrollment}
            requestTypeId={RequestTypeEnum.Kindergarten}
          />
        </SidebarNavigationItem>
      </SidebarNavigationItem>
    );
  };

  const getSubsequentEnrollmentList = () => {
    return (
      <SidebarNavigationItem
        name={t("sidebar.subsequentEnrollment")}
        key={`navigation_subsequentEnrollment`}
        expanded={
          pathname === subsequentEnrollmentListRoute ||
          pathname === subsequentConfirmedEnrollmentListRoute ||
          pathname === subsequentRejectedEnrollmentListRoute
        }
      >
        <SidebarNavigationItem
          key={`navigation_subsequentEnrollmentListChildren`}
          name={t("sidebar.enrollmentChildren")}
          route={subsequentEnrollmentListRoute}
        >
          <EnrollmentListPage
            requestTypeId={RequestTypeEnum.Kindergarten}
            enrollmentTypeId={EnrollmentTypeEnum.SubsequentEnrollment}
          />
        </SidebarNavigationItem>
        <SidebarNavigationItem
          key="navigation_subsequentConfirmedEnrollmentList"
          name={t("sidebar.confirmedEnrollmentList")}
          route={subsequentConfirmedEnrollmentListRoute}
        >
          <ConfirmedEnrollmentListPage
            requestTypeId={RequestTypeEnum.Kindergarten}
            enrollmentTypeId={EnrollmentTypeEnum.SubsequentEnrollment}
          />
        </SidebarNavigationItem>
        <SidebarNavigationItem
          key="navigation_subsequentRejectedEnrollmentList"
          name={t("sidebar.rejectedEnrollmentList")}
          route={subsequentRejectedEnrollmentListRoute}
        >
          <RejectedEnrollmentListPage
            requestTypeId={RequestTypeEnum.Kindergarten}
            enrollmentTypeId={EnrollmentTypeEnum.SubsequentEnrollment}
          />
        </SidebarNavigationItem>
      </SidebarNavigationItem>
    );
  };

  const getPreschoolEnrollmentList = () => {
    return (
      <SidebarNavigationItem
        name={t("sidebar.preschoolEnrollment")}
        key={`navigation_preschoolEnrollment`}
        expanded={
          pathname === preschoolEnrollmentListRoute ||
          pathname === preschoolConfirmedEnrollmentListRoute ||
          pathname === preschoolRejectedEnrollmentListRoute
        }
      >
        <SidebarNavigationItem
          key={`navigation_preschoolEnrollmentListChildren`}
          name={t("sidebar.enrollmentChildren")}
          route={preschoolEnrollmentListRoute}
        >
          <EnrollmentListPage
            enrollmentTypeId={EnrollmentTypeEnum.RegularEnrollment}
            requestTypeId={RequestTypeEnum.Preschool}
          />
        </SidebarNavigationItem>
        <SidebarNavigationItem
          key="navigation_preschoolConfirmedEnrollmentList"
          name={t("sidebar.confirmedEnrollmentList")}
          route={preschoolConfirmedEnrollmentListRoute}
        >
          <ConfirmedEnrollmentListPage
            enrollmentTypeId={EnrollmentTypeEnum.RegularEnrollment}
            requestTypeId={RequestTypeEnum.Preschool}
          />
        </SidebarNavigationItem>
        <SidebarNavigationItem
          key="navigation_preschoolRejectedEnrollmentList"
          name={t("sidebar.rejectedEnrollmentList")}
          route={preschoolRejectedEnrollmentListRoute}
        >
          <RejectedEnrollmentListPage
            enrollmentTypeId={EnrollmentTypeEnum.RegularEnrollment}
            requestTypeId={RequestTypeEnum.Preschool}
          />
        </SidebarNavigationItem>
      </SidebarNavigationItem>
    );
  };

  const getPreschoolRequestItems = () => {
    return (
      <SidebarNavigationItem
        key={`navigation_preschoolRequestListFirst`}
        name={t("sidebar.preschoolRequest")}
        route={preschoolRequestsRoute}
      >
        <RegularEnrollmentRequestList
          requestTypeId={RequestTypeEnum.Preschool}
        />
      </SidebarNavigationItem>
    );
  };

  const getPreschoolRequestDetailsItems = () => {
    return (
      <SidebarNavigationItem
        key={`navigation_preschoolRequestList`}
        name={t("sidebar.preschoolRequest")}
        expanded
        arrowHiden
        clickable={false}
        customTitle={sidebarTitle()}
      >
        <SidebarNavigationItem
          key={`navigation_${preschoolRequestMatch?.path}basic-info`}
          name={t("sidebar.basicInfo")}
          route={
            preschoolRequestMatch?.path.endsWith("/")
              ? `${preschoolRequestMatch?.path}basic-info`
              : `${preschoolRequestMatch?.path}/basic-info`
          }
          url={
            preschoolRequestMatch?.url.endsWith("/")
              ? `${preschoolRequestMatch?.url}basic-info`
              : `${preschoolRequestMatch?.url}/basic-info`
          }
        >
          <RequestLayout requestPersonInfo={requestPersonInfo}>
            <RequestBasicInfoPage></RequestBasicInfoPage>
          </RequestLayout>
        </SidebarNavigationItem>
        <SidebarNavigationItem
          name={t("sidebar.locationPrograms")}
          route={
            preschoolRequestMatch?.path.endsWith("/")
              ? `${preschoolRequestMatch?.path}location-programs`
              : `${preschoolRequestMatch?.path}/location-programs`
          }
          url={
            preschoolRequestMatch?.url.endsWith("/")
              ? `${preschoolRequestMatch?.url}location-programs`
              : `${preschoolRequestMatch?.url}/location-programs`
          }
        >
          <RequestLayout requestPersonInfo={requestPersonInfo}>
            <LocationProgramDetails />
          </RequestLayout>
        </SidebarNavigationItem>
        <SidebarNavigationItem
          name={t("sidebar.criteriaAndPoints")}
          route={
            preschoolRequestMatch?.path.endsWith("/")
              ? `${preschoolRequestMatch?.path}criteria`
              : `${preschoolRequestMatch?.path}/criteria`
          }
          url={
            preschoolRequestMatch?.url.endsWith("/")
              ? `${preschoolRequestMatch?.url}criteria`
              : `${preschoolRequestMatch?.url}/criteria`
          }
        >
          <RequestLayout requestPersonInfo={requestPersonInfo}>
            <RequestCriteriaPage
              requestPersonInfo={requestPersonInfo}
            ></RequestCriteriaPage>
          </RequestLayout>
        </SidebarNavigationItem>
        <SidebarNavigationItem
          name={t("sidebar.initialConversation")}
          route={
            preschoolRequestMatch?.path.endsWith("/")
              ? `${preschoolRequestMatch?.path}initial-conversation`
              : `${preschoolRequestMatch?.path}/initial-conversation`
          }
          url={
            preschoolRequestMatch?.url.endsWith("/")
              ? `${preschoolRequestMatch?.url}initial-conversation`
              : `${preschoolRequestMatch?.url}/initial-conversation`
          }
        >
          <RequestLayout requestPersonInfo={requestPersonInfo}>
            <RequestInitialConversationPage
              requestPersonInfo={requestPersonInfo}
            ></RequestInitialConversationPage>
          </RequestLayout>
        </SidebarNavigationItem>
        {hasPermission(PermissionEnum.PhysicalExaminationRead) ? (
          <SidebarNavigationItem
            name={t("sidebar.physicalExamination")}
            route={
              preschoolRequestMatch?.path.endsWith("/")
                ? `${preschoolRequestMatch?.path}physical-examination`
                : `${preschoolRequestMatch?.path}/physical-examination`
            }
            url={
              preschoolRequestMatch?.url.endsWith("/")
                ? `${preschoolRequestMatch?.url}physical-examination`
                : `${preschoolRequestMatch?.url}/physical-examination`
            }
          >
            <RequestLayout requestPersonInfo={requestPersonInfo}>
              <RequestPhysicalExaminationPage
                requestPersonInfo={requestPersonInfo}
              ></RequestPhysicalExaminationPage>
            </RequestLayout>
          </SidebarNavigationItem>
        ) : null}
      </SidebarNavigationItem>
    );
  };

  return (
    <>
      <div className={styles.buttonContent}>
        <div>
          <ArrowButton
            ariaLabel={t("accessibility.backButton")}
            backwards
            onClick={handleBackToEUpisi}
          ></ArrowButton>
        </div>
        <Typography
          variant="base"
          color="primary"
          className={styles.buttonText}
        >
          {t("sidebar.backToEUpisi")}
        </Typography>
      </div>
      <SidebarNavigationContainer color="base" size="small" includeUnauthPage>
        <SidebarNavigationItem
          name={t("sidebar.home")}
          key={`navigation_home`}
          route={homeRoute}
          exact={!showAboutPage && !showIzjavaOPristupacnostiPage}
        >
          {showAboutPage ? (
            <AboutProgram />
          ) : showIzjavaOPristupacnostiPage ? (
            <IzjavaOPristupacnosti />
          ) : (
            <HomePage />
          )}
        </SidebarNavigationItem>
        {canReadBeneficiaryList() && (
          <SidebarNavigationItem
            name={t("sidebar.beneficiary")}
            expanded={pathname === beneficiaryListRoute}
          >
            {geBeneficiaryListItems()}
          </SidebarNavigationItem>
        )}
        {canReadImportantDates() && (
          <SidebarNavigationItem
            name={t("sidebar.importantDates")}
            expanded={
              pathname === importantDateListRoute ||
              pathname === subsequentImportantDateListRoute ||
              editImportantDateMatch != null ||
              editSubsequentImportantDateMatch != null
            }
          >
            {getImportantDateItems()}
            {getSubsequentImportantDateItems()}
          </SidebarNavigationItem>
        )}
        {hasPermission(PermissionEnum.CriterionRead) ||
        hasPermission(PermissionEnum.BenefitRead) ? (
          <SidebarNavigationItem
            name={t("sidebar.criteria")}
            expanded={
              pathname === criteriaRoute ||
              pathname === benfitsRoute ||
              newCriterionMatch != null ||
              editCriterionMatch != null ||
              newBenefitMatch != null ||
              detailsBenefitMatch != null
            }
          >
            {hasPermission(PermissionEnum.CriterionRead)
              ? getCriteriaItems()
              : null}

            {/* {hasPermission(PermissionEnum.BenefitRead)
              ? getBenefitsItems()
              : null} */}
          </SidebarNavigationItem>
        ) : null}

        {hasPermission(PermissionEnum.LocationProgramDataRead) ? (
          <SidebarNavigationItem
            name={t("sidebar.capacities")}
            expanded={
              pathname === capacitiesRoute ||
              pathname === subsequentCapacitiesRoute ||
              newCapacityMatch != null ||
              editCapacityMatch != null ||
              newSubsequentCapacityMatch != null ||
              editSubsequentCapacityMatch != null
            }
          >
            {getCapacityItems()}
            {getSubsequentCapacityItems()}
          </SidebarNavigationItem>
        ) : null}
        {hasPermission(PermissionEnum.RequestRead) ? (
          <SidebarNavigationItem
            name={t("sidebar.requests")}
            key={`navigation_request`}
            expanded={
              pathname === requestsRoute ||
              pathname === subsequentRequestsRoute ||
              /*pathname === preschoolRequestsRoute ||*/
              requestMatch?.path === requestDetailsRoute ||
              subsequentRequestMatch?.path === subsequentRequestDetailsRoute
              //|| preschoolRequestMatch?.path === preschoolRequestDetailsRoute
            }
          >
            {requestMatch ? getRequestDetailsItems() : getRequestItems()}
            {subsequentRequestMatch
              ? getSequentRequestDetailsItems()
              : getSubsequentRequestItems()}

            {/*{preschoolRequestMatch*/}
            {/* ? getPreschoolRequestDetailsItems()*/}
            {/* : getPreschoolRequestItems()} */}
          </SidebarNavigationItem>
        ) : null}
        {hasPermission(PermissionEnum.EnrollmentListRead) ? (
          <SidebarNavigationItem
            name={t("sidebar.enrollmentList")}
            key={`navigation_enrollmentList`}
            expanded={
              pathname === enrollmentListRoute ||
              pathname === confirmedEnrollmentListRoute ||
              pathname === rejectedEnrollmentListRoute ||
              pathname === subsequentEnrollmentListRoute ||
              pathname === subsequentConfirmedEnrollmentListRoute ||
              pathname === subsequentRejectedEnrollmentListRoute //||
              //pathname === preschoolEnrollmentListRoute ||
              //pathname === preschoolConfirmedEnrollmentListRoute ||
              //pathname === preschoolRejectedEnrollmentListRoute
            }
          >
            {getEnrollmentList()}
            {getSubsequentEnrollmentList()}
            {/*{getPreschoolEnrollmentList()}*/}
          </SidebarNavigationItem>
        ) : null}
        {hasPermission(PermissionEnum.InitialConversationEnabledUpdate) ? (
          <SidebarNavigationItem
            name={t("sidebar.initialConversationTitle")}
            expanded={pathname === initialConversationRoute}
          >
            {hasPermission(PermissionEnum.InitialConversationEnabledUpdate) ? (
              <SidebarNavigationItem
                name={t("sidebar.initialConversationTitle")}
                route={initialConversationRoute}
              >
                <InitialConversation></InitialConversation>
              </SidebarNavigationItem>
            ) : null}
          </SidebarNavigationItem>
        ) : null}
      </SidebarNavigationContainer>
    </>
  );
};
